import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import TableauVisualization from './tableau-visualization';
import graphicUrl from '../assets/us-states-by-archetype.jpg';
import './article.css';

const ArticleDetails = {
  articleLink: 'https://journals.aom.org/doi/10.5465/amd.2019.0064',
  title: 'Coevolution of Platform Dominance and Governance Strategies',
  subtitle: 'Effects on Complementor Performance Outcomes',
  datePublished: '9 June 2020',
  doi: 'DOI 10.5465/amd.2019.0064',
  doiLink: 'https://doi.org/10.5465/amd.2019.0064'
};

const getMobile = () => window.innerWidth <= 527;
const mobileWarningKey = 'hideMobileWarning';

export default function Article() {
  const [showMobileNotice, setMobileNotice] = useState(
    window.localStorage.getItem(mobileWarningKey) !== 'true'
  );
  const [isMobile, setMobile] = useState(getMobile());

  useEffect(() => {
    function handleResize() {
      const nextMobile = getMobile();
      if (nextMobile !== isMobile) {
        setMobile(nextMobile);
      }
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  useEffect(() => {
    if (window.tableau) {
      window.tableau.vizs.forEach(v => {
        v.refresh();
      });
    }
  }, [isMobile]);

  function hideMobileNotice() {
    window.localStorage.setItem(mobileWarningKey, 'true');
    setMobileNotice(false);
  }

  return (
    <article className="Article">
      <a
        className="Article-headerReturnLink container wide"
        href={ArticleDetails.articleLink}
      >
        Return to Academy of Management Discoveries
      </a>
      <header className="Article-header container wide">
        <h4 className="Article-notes">
          {ArticleDetails.datePublished} <span>&middot;</span>{' '}
          <a href={ArticleDetails.doiLink}>{ArticleDetails.doi}</a>
        </h4>
        <h1 className="Article-title">{ArticleDetails.title}</h1>
        <h2 className="Article-subtitle">{ArticleDetails.subtitle}</h2>
        <h3 className="Article-authors">
          By <span>Joost Rietveld</span>, <span>Joe N Ploog</span>, and{' '}
          <span>David B Nieborg</span>
        </h3>
      </header>
      <div className="container">
        <p className="Article-notice">
          This web page is a supplement to the above article which can be read
          at the{' '}
          <a href={ArticleDetails.articleLink}>
            Academy of Management Discoveries
          </a>
          .
        </p>
      </div>
      <div className="Article-content">
        {isMobile && showMobileNotice && (
          <div className="MobileNotice">
            <p>
              <strong>Note:</strong> the visualizations on this page are
              designed to be viewed on a desktop computer.
            </p>
            <p>
              <button type="button" onClick={hideMobileNotice}>
                Okay
              </button>
            </p>
          </div>
        )}
        <p>
          These five charts explore various evolutionary trends in four
          market-leading platform ecosystems: Apple’s iOS (mobile applications),
          Kickstarter (creative projects), Kiva (microloans), and Valve’s Steam
          (PC video games). Each chart looks at a different aspect of the
          platform’s evolution.
        </p>
        <h3>All Platforms: End Users and Complement Growth</h3>
        <figure>
          <TableauVisualization
            id="viz1597067308055"
            path="1_AllplatformsJuly2020Revised/Dashboard3"
            placeholderImagePath="1_AllplatformsJuly2020Revised/Dashboard3/1_rss.png"
            width={800}
            height={645}
            minHeight={300}
            isMobile={isMobile}
            loadOrder={1}
          />
        </figure>
        <p>
          The first set of charts looks at the accumulation of end users and
          complements (e.g., apps, projects, loans and games) on each of the
          four platforms in our sample. The charts show that the two user groups
          are strongly correlated and have steadily increased on all four
          platforms. Different platforms can be selected from the dropdown menu
          in the chart.
        </p>
        <h3>Steam: New Games Before & After Greenlight</h3>
        <figure>
          <TableauVisualization
            id="viz1597109582313"
            path="2_SteamgreenlightJuly2020/Dashboard1"
            placeholderImagePath="2_SteamgreenlightJuly2020/Dashboard1/1_rss.png"
            width={800}
            height={840}
            minHeight={700}
            isMobile={isMobile}
            loadOrder={2}
          />
        </figure>
        <p>
          The second chart displays the number of new games joining Steam before
          and after the introduction of Greenlight in August, 2012. Greenlight
          is a governance change implemented by Valve, which crowdsources (part
          of) the screening process for new games looking to enter the platform.
          The data show that new entry of games increased substantially after
          the introduction of Greenlight.
        </p>
        <h3>Kickstarter: Entry and Success Rates</h3>
        <figure>
          <TableauVisualization
            id="viz1597109691970"
            path="3_KickstartersuccessJuly2020/Dashboard1"
            placeholderImagePath="3_KickstartersuccessJuly2020/Dashboard1/1.png"
            width={800}
            height={860}
            minHeight={800}
            isMobile={isMobile}
            loadOrder={3}
          />
        </figure>
        <p>
          The top panel in the third chart depicts projects’ average success
          rates on Kickstarter before and after the implementation of Launch Now
          in June, 2014. Similar to Greenlight, Launch Now made it easier for
          projects to enter the platform as Kickstarter automated various
          screening processes. The average success rate (or, the share of
          projects reaching their funding goal) dropped following this
          governance change. The bottom panel shows the number of successful
          projects in two funding categories; projects with a funding goal above
          and below $100,000 USD. Relatively more projects with a funding goal
          above $100,000 USD achieved their funding goal, and the gap between
          the two categories is grew wider as Kickstarter implemented changes to
          highlight certain projects.
        </p>
        <h3>Steam: Cumulative Downloads by Download Ranks</h3>
        <figure id="figure-count">
          <TableauVisualization
            id="viz1597109761563"
            path="4_SteamtopbottomJuly2020/Dashboard1"
            placeholderImagePath="4_SteamtopbottomJuly2020/Dashboard1/1.png"
            width={800}
            height={830}
            minHeight={600}
            isMobile={isMobile}
            loadOrder={4}
          />
        </figure>
        <p>
          The fourth chart displays the distribution of downloads for different
          game cohorts on Steam. For example, the top 20th percentile most
          downloaded video games released in 2007 captured 66% of all downloads
          for games released in 2007. The data show that the top performing
          games in a year increasingly captured a larger share of all downloads
          for games released in the same year. The tooltip reports the median
          downloads, top downloads and bottom downloads for games within a bar.
          In 2014, Valve implemented a Discovery Update. This algorithmic
          governance change resulted in the most popular games on the platform
          being prominently featured on the platform’s store front.
        </p>
        <h3>iOS App Store: Prices & Costs for Game Apps</h3>
        <figure>
          <TableauVisualization
            id="viz1597109893580"
            path="5_AppstorepricesandcostsJuly2020/Dashboard2"
            placeholderImagePath="5_AppstorepricesandcostsJuly2020/Dashboard2/1.png"
            width={800}
            height={850}
            minHeight={800}
            isMobile={isMobile}
            loadOrder={5}
          />
        </figure>
        <p>
          The top panel in the fifth graph shows the average price for game apps
          on the iOS App Store and the number of game apps launched on the
          platform in each year. The data show that the average price of apps
          declined from $1.37 USD in 2009 to $0.55 USD in 2016. The tooltip on
          the bars provides a breakdown of the share of game apps across
          different price categories. The bottom panel displays the average
          revenue per loyal app user and the average costs to acquire a loyal
          app user (or, cost per install) for game apps in the iOS App Store.
          The data show that user acquisition costs have increased at a
          disproportionate rate relative to the revenues generated from loyal
          users.
        </p>
      </div>
      <footer className="Article-footer container">
        <p>
          The data used in this research come from various sources. For more
          information on the data and how platform governance affects complement
          performance, read{' '}
          <a href={ArticleDetails.articleLink}>the full paper</a>.
        </p>
      </footer>
    </article>
  );
}
